/* PreNumbers Financial Builder — reskinned shell + context.
   REPLACES: src/components/FinancialBuilder/FinancialBuilderPage.jsx

   Keeps your context shape and data flow EXACTLY (pageNum, companies,
   companySelected/updateCompany, spotRates, messageApi, getAllUserCompanies).
   Adds: a left-sidebar app shell, a topbar, a `goTo` helper, and an optional
   theme toggle. Page flow unchanged: 1 Companies -> 2 Products -> 3 Dashboard. */

import { useState, createContext, useEffect, useContext } from 'react';
import { fetchWithAccessTokenRetry } from '../helperFunctions.jsx';
import { message } from 'antd';
import {
  ApartmentOutlined, AppstoreOutlined, DashboardOutlined,
  DownOutlined, BulbOutlined, BulbFilled,
} from '@ant-design/icons';
import { ThemeToggle } from '../Navbar/Navbar.jsx';
import { Link } from 'react-router';

import ProductsPage from './ProductsPage/ProductsPage.jsx';
import CompaniesPage from './CompaniesPage/CompaniesPage.jsx';
import Dashboard from './DashboardOverview/Dashboard.jsx';

/* Reskin theme. Tokens come from styles/colors_and_type.css (imported app-wide).
   These CriticalFinance sheets are no longer needed by the reskinned screens —
   keep them only if something else on this route still relies on them. */
import './financial-builder.css';

export const FinancialBuilderContext = createContext(null);

/* ------------------------------- shell pieces ------------------------------ */
function Sidebar() {
  const { pageNum, goTo, companySelected, companies } = useContext(FinancialBuilderContext);
  const hasProducts = !!companySelected && (companySelected.products?.length > 0);
  const nav = [
    { n: 1, label: 'Companies', icon: <ApartmentOutlined />, enabled: true },
    { n: 2, label: 'Products', icon: <AppstoreOutlined />, enabled: !!companySelected },
    { n: 3, label: 'Dashboard', icon: <DashboardOutlined />, enabled: hasProducts },
  ];
  return (
    <aside className="pn-side">
      <div className="pn-brand">
        <Link to="/" className="navbar-logo">
          <img className="navbar-logo-img" src="/imgs/logo.png" alt="PreNumbers" />
          <span className="navbar-logo-text">PreNumbers</span>
        </Link>
      </div>
      <div className="pn-nav-group-label">Financial Builder</div>
      {nav.map((item) => (
        <div key={item.n}
          className={"pn-nav-item" + (pageNum === item.n ? " active" : "")}
          style={item.enabled ? undefined : { opacity: 0.4, pointerEvents: 'none' }}
          onClick={() => item.enabled && goTo(item.n)}>
          {item.icon}<span>{item.label}</span>
        </div>
      ))}
      <div className="pn-nav-spacer" />
      <div className="pn-side-card">
        <div className="lbl">Active model</div>
        <div className="val">{companySelected ? companySelected.name : 'None selected'}</div>
        <div className="lbl" style={{ marginTop: 8 }}>Workspace</div>
        <div className="val">{companies.length} companies</div>
      </div>
    </aside>
  );
}

function Topbar() {
  const { pageNum, companySelected, theme, setTheme } = useContext(FinancialBuilderContext);
  const crumb = ['Companies', 'Products', 'Dashboard'][pageNum - 1] || 'Companies';
  return (
    <header className="pn-topbar">
      <div className="pn-crumbs"><span>Financial Builder</span><span className="sep">/</span><span className="cur">{crumb}</span></div>
      <div className="pn-topbar-spacer" />
      {companySelected && (
        <div className="pn-company-switch"><span className="pn-company-dot" />{companySelected.name}<DownOutlined style={{ fontSize: 11 }} /></div>
      )}
      <ThemeToggle />
    </header>
  );
}

function PageSwitcher() {
  const { pageNum } = useContext(FinancialBuilderContext);
  switch (pageNum) {
    case 1: return <CompaniesPage />;
    case 2: return <ProductsPage />;
    case 3: return <Dashboard />;
    default: return <CompaniesPage />;
  }
}

/* --------------------------------- data ----------------------------------- */
async function getAllUserCompanies(setCompanies, setSpotRates, messageApi) {
  messageApi.info('Loading companies...');
  try {
    const response = await fetchWithAccessTokenRetry('/api/fb/getCompanies');
    if (!response.ok) throw new Error('Failed to fetch companies');
    const data = await response.json();
    setCompanies(data.companies);
    setSpotRates(data.spotRates);
    messageApi.destroy();
    messageApi.success('Companies loaded successfully');
  } catch (error) {
    console.error('Error fetching user companies:', error);
    setCompanies([]);
    messageApi.destroy();
    messageApi.error('Failed to load companies');
  }
}

function FinancialBuilderPage() {
  const [pageNum, setPageNum] = useState(1);
  const [messageApi, contextHolder] = message.useMessage();
  const [companies, setCompanies] = useState([]);
  const [companySelectedID, setCompanySelectedID] = useState("");
  const [companySelected, setCompanySelected] = useState(null);
  const [spotRates, setSpotRates] = useState(null);
  const [theme, setThemeState] = useState(() =>
    (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark');

  // Atomic updater: edits to the selected company flow back into the list.
  const updateCompany = (updater) => {
    setCompanySelected((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      setCompanies((prevCompanies) => {
        const idx = prevCompanies.findIndex((e) => e.id === next.id);
        if (idx === -1) return prevCompanies;
        const nextCompanies = [...prevCompanies];
        nextCompanies[idx] = next;
        return nextCompanies;
      });
      return next;
    });
  };

  const goTo = (n) => {
    setPageNum(n);
    const c = document.querySelector('.pn-canvas');
    if (c) c.scrollTop = 0;
  };

  // The light-theme variables in colors_and_type.css live on the document root,
  // so the toggle flips the global attribute. If you manage theme elsewhere,
  // delete setTheme + the topbar toggle and this stays dark.
  const setTheme = (t) => {
    setThemeState(t);
    document.documentElement.setAttribute('data-theme', t);
    document.body.setAttribute('data-theme', t);
  };

  const contextToUse = {
    pageNum, setPageNum, goTo, messageApi,
    companies, setCompanies,
    companySelectedID, setCompanySelectedID,
    companySelected, setCompanySelected: updateCompany,
    spotRates, theme, setTheme,
  };

  useEffect(() => { getAllUserCompanies(setCompanies, setSpotRates, messageApi); }, [messageApi]);

  useEffect(() => {
    if (companySelectedID !== "-- Select a company --") {
      const selected = companies.find((c) => c.id === parseInt(companySelectedID));
      setCompanySelected(selected);
    }
  }, [companySelectedID, companies]);

  return (
    <>
      {contextHolder}
      <FinancialBuilderContext.Provider value={contextToUse}>
        <div className="pn-app">
          <Sidebar />
          <div className="pn-main">
            <Topbar />
            <div className="pn-canvas"><PageSwitcher /></div>
          </div>
        </div>
      </FinancialBuilderContext.Provider>
    </>
  );
}

export default FinancialBuilderPage;
